/**
 * THE ONE EMPTY MARK — what a cell prints when there is nothing to print.
 *
 * 🔴 **IT EXISTS BECAUSE EVERFIT HAS TWO OF THEM AND WE SHOULD NOT INHERIT THAT**
 * (`docs/EVERFIT-MAP.md` §2-ter, read live 2026-09-19): their ROSTER prints `--` (two hyphens)
 * and their METRICS table prints `—` (an em dash), for the same idea, in the same product, two
 * clicks apart. Fable's ruling on W19(d): ours picks ONE and says which. It is the EM DASH.
 *
 * ⚠️ WHY THE EM DASH AND NOT THEIR `--`: two hyphens are a typewriter's em dash, they are read
 * aloud as "hyphen hyphen" by some screen readers, and beside a column of tabular numerals they
 * look like a minus sign with a stutter. The em dash is the glyph the rest of this product's
 * prose already uses. (Every site that prints it also carries a visually-hidden SENTENCE — a
 * screen reader cannot see a dash, and "nothing" and "nothing assigned" are different answers.)
 *
 * ── THE RULE, WHICH IS THEIRS AND IS SHARPER THAN A PERCENTAGE ──────────────────────────────
 *
 * 🔴 **THEY DISTINGUISH `-%` FROM `0%` AND THAT DISTINCTION IS THE WHOLE POINT** — a dash when
 * nothing was ASSIGNED in the window, a zero when something was and none of it was tracked. Two
 * different messages to a coach: "I have not written them anything" and "I wrote them four
 * sessions and they did none". Their roster's bare `0%` cannot tell them apart, which is the
 * defect `docs/NEXT-WORK.md` W19(d) records; their client page's own `0/0` and `0/1` can, and
 * ours matches their better half.
 *
 * ⚠️ **AND OURS KEEPS ONE ANSWER THEIRS CANNOT EXPRESS**: somebody who TRAINED with nothing
 * assigned. `completed / assigned` renders that as `3 / 0`; a percentage divides by zero and can
 * only say `-%`. So the predicate is "nothing happened at all", never "nothing was assigned" —
 * collapsing it to the latter would hide real training behind a dash, which is the one direction
 * that costs a coach a conversation.
 */

/** The mark. One string, one definition, every screen. */
export const EMPTY_MARK = "—";

/** A window's two numbers. `completed` is what was tracked; `assigned` is what was given. */
export type Window = { completed: number; assigned: number };

/**
 * Does this window have nothing at all to say?
 *
 * ⚠️ BOTH HALVES, DELIBERATELY. `assigned === 0` alone would mark a window in which the athlete
 * trained off their own bat — see the note above. Nothing given AND nothing done is the only
 * state with no number worth printing.
 */
export function isEmptyWindow(w: Window): boolean {
  return w.assigned === 0 && w.completed === 0;
}

/**
 * The cell's text: the mark, or `completed / assigned`.
 *
 * Returned as a STRING rather than as JSX so it can be executed by a test and so the roster and
 * the client page cannot render the same window two ways — which is what they did before this
 * file existed, with the rule typed out at seven call sites.
 */
export function windowCell(w: Window): string {
  return isEmptyWindow(w) ? EMPTY_MARK : `${String(w.completed)} / ${String(w.assigned)}`;
}
